import { format, parseISO } from "date-fns";
import React, { useMemo, useState } from "react";
import { z } from "zod";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { faFilePdf } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	ButtonStandard,
	FormDatePicker,
	FormField,
	FormInputText,
	FormSubscribe,
	Icon,
	ModalBase,
	PYForm,
	SubmitButton,
	Text,
	toast,
} from "@performyard/py-components";
import type { WorkflowDetailTodoListItem } from "@performyard/service-contracts";

import { Attachment } from "../../../components/Attachment";
import { MultipleSelectEmployee } from "../../../components/Inputs/SelectEmployee";
import SignatureDocumentPicker, { type SignatureSelection } from "./SignatureDocumentPicker";

import { useSaveWorkflowTodo } from "../../../api/hooks";

import { describeWorkflowActionError } from "../../../api/services/defs/workflows";

import { NamedReferenceSchema } from "../todoFilterState";

/**
 * @param requireAssignees False once a signature request is in play. The signing wizard
 * picks the signers and creates a to-do for each of them, so an assignee chosen here
 * would never reach a to-do — requiring one asks for an answer that is thrown away.
 *
 * The return type is left inferred deliberately: both `assignees` branches are the same
 * ZodArray type (`.min()` adds a runtime check, not a type), so there is one schema type
 * here, and spelling it out means either restating the whole shape or casting.
 */
const buildTodoFormSchema = (requireAssignees: boolean) =>
	z.object({
		name: z.string().trim().min(1, "This is a required field").max(256, "Name must be 256 characters or less"),
		assignees: requireAssignees
			? z.array(NamedReferenceSchema).min(1, "This is a required field")
			: z.array(NamedReferenceSchema),
		dueDate: z
			.date({ error: "This is a required field" })
			.nullable()
			.refine((value): value is Date => value !== null, "This is a required field"),
	});

type TodoForm = z.infer<ReturnType<typeof buildTodoFormSchema>>;

/**
 * The attached signature request: the document and, when known, how many signers it
 * takes. Shared by the add and edit flows.
 *
 * The card is the same `Attachment` the AI competency importer uses for an uploaded
 * document, so the two read as one component rather than two takes on the same idea.
 * Nothing but the remove control is clickable: swapping the document is remove-then-pick,
 * which is also the only shape the picker below supports, and `onRemove` is passed only
 * while the pick is still uncommitted — a request the signing wizard has already created
 * cannot be changed from here at all (see the modal's note on locking below).
 */
const SignatureRequestSummary = ({
	name,
	signerCount,
	onRemove,
}: {
	name: string;
	/** Null when the source defines no signers yet, which hides the count line. */
	signerCount: number | null;
	onRemove?: () => void;
}) => (
	<div className="flex flex-col gap-1.5">
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			Signature Request
		</Text>
		<Attachment
			state="idle"
			icon={faFilePdf}
			title={name}
			description={
				signerCount === null ? undefined : `${signerCount} ${signerCount === 1 ? "Signer" : "Signers"}`
			}
			onRemove={onRemove}
			testId="attachedSignatureRequest"
			removeTestId="removeSignatureRequestButton"
			removeAriaLabel={`Remove ${name}`}
		/>
	</div>
);

const describeSaveOutcome = (isEditing: boolean, assigneeCount: number): string => {
	if (!isEditing) {
		return assigneeCount > 1 ? `${assigneeCount} To-Dos created` : "To-Do created";
	}
	// One of the assignees is the to-do being edited; the rest are new to-dos.
	const createdCount = assigneeCount - 1;
	if (createdCount === 0) {
		return "To-Do updated";
	}
	return `To-Do updated and ${createdCount} ${createdCount === 1 ? "To-Do" : "To-Dos"} created`;
};

export const buildSignatureSetupUrl = ({
	selection,
	workflowId,
	todoName,
	dueDate,
	returnTo,
}: {
	selection: SignatureSelection;
	workflowId: string;
	todoName: string;
	dueDate: Date;
	returnTo: string;
}): string => {
	const params = new URLSearchParams({
		wid: workflowId,
		due: format(dueDate, "MMddyyyy"),
		emps: "",
		todo_name: todoName,
		return_to: returnTo,
	});

	return selection.kind === "template"
		? `/doc-signing/setup/?template=${selection.id}&${params.toString()}`
		: `/doc-signing/setup/${selection.id}/?${params.toString()}`;
};

type TodoFormModalProps = {
	workflowId: string;
	isOpen: boolean;
	onClose: () => void;
	/** When set, the modal edits this to-do instead of creating a new one. */
	todo?: WorkflowDetailTodoListItem | null;
	/** Company doc-signing is enabled, so attaching a signature request is offered. */
	allowSignature: boolean;
	/** The workflow's subject, whose documents the picker lists in their own folder. */
	subject: { _id: string; name: string } | null;
};

const TodoFormModal = ({ workflowId, isOpen, onClose, todo, allowSignature, subject }: TodoFormModalProps) => {
	const isEditing = !!todo;
	const { mutateAsync: saveTodo } = useSaveWorkflowTodo();

	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [signatureSelection, setSignatureSelection] = useState<SignatureSelection | null>(null);

	// A to-do with a signature request always shows its document, never the Add button.
	// `hasSignatureRequest` is the trigger because it is true whenever the to-do carries a
	// `signature_request` id; the `signatureRequest` summary alongside it may still be
	// null (an unresolvable reference, or an API that predates the field), which only
	// costs the name and count — not the card itself.
	const hasAttachedSignature = !!todo?.hasSignatureRequest;
	const attachedSignatureRequest = todo?.signatureRequest ?? null;

	// A saved request wins: it is the one the wizard already built, and it can be neither
	// swapped nor removed from here. Otherwise the pick made in this session shows, which
	// is still free to remove — a document has no signers until the wizard assigns them,
	// so its count stays null rather than showing a misleading 0.
	const signatureSummary = hasAttachedSignature
		? {
				name: attachedSignatureRequest?.name ?? "Signature request attached",
				signerCount: attachedSignatureRequest?.signerCount ?? null,
				onRemove: undefined,
			}
		: signatureSelection
			? {
					name: signatureSelection.name,
					signerCount: signatureSelection.signerCount,
					onRemove: () => setSignatureSelection(null),
				}
			: null;

	const showAssignees = !signatureSelection;

	const canAttachSignature = allowSignature && !isEditing;
	const schema = useMemo(() => buildTodoFormSchema(showAssignees), [showAssignees]);

	if (!isOpen) {
		return null;
	}

	const defaultValues: TodoForm = {
		name: todo?.action ?? "",
		assignees: todo?.actor ? [{ id: todo.actor._id, name: todo.actor.name }] : [],
		dueDate: todo?.dueDate ? parseISO(String(todo.dueDate).slice(0, 10)) : null,
	};

	const handleClose = () => {
		setIsPickerOpen(false);
		setSignatureSelection(null);
		onClose();
	};

	const handleSubmit = async ({ value }: { value: TodoForm }) => {
		const assignees = value.assignees;
		const dueDate = value.dueDate as Date;

		if (signatureSelection) {
			window.location.assign(
				buildSignatureSetupUrl({
					selection: signatureSelection,
					workflowId,
					todoName: value.name,
					dueDate,
					returnTo: `${window.location.pathname}${window.location.search}`,
				})
			);
			return;
		}

		const editedIndex = isEditing
			? Math.max(
					0,
					assignees.findIndex((assignee) => assignee.id === todo?.actor?._id)
				)
			: -1;

		try {
			// Sequential rather than parallel so a failure part-way leaves a predictable
			// prefix saved rather than a random subset, and the error names how far it got.
			for (const [index, assignee] of assignees.entries()) {
				await saveTodo({
					workflowId,
					todoId: index === editedIndex ? todo?._id : undefined,
					name: value.name,
					actorId: assignee.id,
					dueDate: format(dueDate, "yyyy-MM-dd"),
				});
			}
			toast.success(describeSaveOutcome(isEditing, assignees.length));
			handleClose();
		} catch (error) {
			toast.error(describeWorkflowActionError(error, "There was an error saving the to-do."));
		}
	};

	return (
		// The `min-h-0` chain from here down makes the document list the only element
		// allowed to shrink, so it is the single scroll region and the modal itself never
		// scrolls. Do not add `overflow-visible` back: it disables ModalBase's max-h-[80vh]
		// and the buttons render outside the modal.
		<ModalBase isOpen onClose={handleClose} dangerouslySetClassName="w-[432px] !overflow-y-hidden">
			<div className="flex min-h-0 w-[382px] flex-col px-6 pb-6">
				<div className="mb-4 text-center">
					<Text size="sub-heading" weight="semi-bold" data-testid="todoFormTitle">
						{isEditing ? "Edit To-Do" : "Add a New To-Do"}
					</Text>
				</div>
				<PYForm
					id="workflow-todo-form"
					schema={schema}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					dangerouslySetClassName="flex min-h-0 flex-col"
				>
					<div className="flex min-h-0 flex-col gap-4">
						<FormInputText id="name" label="To-Do" placeholder="Your text here" clearable />

						{showAssignees && (
							<FormField<{ id: string; name: string }[]>
								id="assignees"
								label="Assignees"
								input={(field) => (
									<MultipleSelectEmployee
										value={field.value ?? []}
										onChange={(employees) => field.onChange(employees)}
										placeholder="Make a selection"
										disabled={hasAttachedSignature}
										data-testid="todoAssigneeSelect"
									/>
								)}
							/>
						)}

						<FormDatePicker id="dueDate" label="Due Date" size="medium" />

						{/* The summary replaces the button once something is attached, and its X puts
						    the button back — that is how an uncommitted pick gets swapped. A saved
						    request has no X at all: the signing wizard owns it, and the way to change
						    it is to delete the to-do and add a new one. */}
						{(signatureSummary || canAttachSignature) && (
							<div className="flex min-h-0 flex-col gap-2">
								{signatureSummary ? (
									<SignatureRequestSummary
										name={signatureSummary.name}
										signerCount={signatureSummary.signerCount}
										onRemove={signatureSummary.onRemove}
									/>
								) : (
									<ButtonStandard
										variant="outline"
										color="primary"
										size="medium"
										onClick={() => setIsPickerOpen((previous) => !previous)}
										aria-expanded={isPickerOpen}
										aria-controls="signature-document-picker"
										data-testid="addSignatureRequestButton"
									>
										<span className="flex items-center justify-center gap-1.5">
											<Icon icon={faPlus} size={12} color="primary-5" />
											Add Signature Request
										</span>
									</ButtonStandard>
								)}

								{isPickerOpen && canAttachSignature && (
									<SignatureDocumentPicker
										subject={subject}
										onSelect={(selection) => {
											setSignatureSelection(selection);
											// Collapse on pick: the summary above now reflects the choice, so leaving
											// the list open would strand a second copy of it under the card.
											setIsPickerOpen(false);
										}}
									/>
								)}
							</div>
						)}
					</div>
					<FormSubscribe id="submitStateSubscription" selector={(state) => [state?.isSubmitting]}>
						{([isSubmitting]) => (
							<div className="mt-6 flex flex-row items-center justify-center gap-2">
								<div className="w-[90px]">
									<ButtonStandard
										variant="outline"
										color="neutral"
										size="large"
										onClick={handleClose}
									>
										Cancel
									</ButtonStandard>
								</div>
								<SubmitButton
									variant="solid"
									color="primary"
									size="large"
									dangerouslySetClassName="w-auto"
									disabled={!!isSubmitting}
									data-testid="todoFormSubmit"
								>
									{isEditing ? "Save Changes" : "Add To-Do"}
								</SubmitButton>
							</div>
						)}
					</FormSubscribe>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default TodoFormModal;
